import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DownloadSimple, FileText, BookOpen, ArrowLeft } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserMenu } from "@/components/UserMenu";
import { normalizeLang } from "@/lib/i18n";
import { toast } from "sonner";

const COPY = {
  en: {
    menu: "My Reports & Downloads",
    heading: "Your Reports & Study Guides",
    intro: "Your Performance Reports and Study Guides are available to download for 30 days after each completed interview. Files are permanently removed after the retention period ends.",
    back: "Back to interview practice",
    empty: "You don't have any retained reports or study guides available to download.",
    quick: "Quick Practice",
    advanced: "Advanced Practice",
    repractice: "Re-practice",
    completed: "Completed",
    expires: "Available until",
    report: "Download Performance Report",
    guide: "Download Study Guide",
    building: "Preparing Study Guide…",
    error: "We couldn't load your downloads. Please try again.",
  },
  "pt-BR": {
    menu: "Meus Relatórios e Downloads",
    heading: "Seus Relatórios e Guias de Estudos",
    intro: "Seus Relatórios de Desempenho e Guias de Estudos ficam disponíveis para download por 30 dias após cada entrevista concluída. Os arquivos são removidos permanentemente após o fim do período de retenção.",
    back: "Voltar à prática de entrevista",
    empty: "Você não tem relatórios ou guias de estudos retidos disponíveis para download.",
    quick: "Prática rápida",
    advanced: "Prática avançada",
    repractice: "Nova prática",
    completed: "Concluída",
    expires: "Disponível até",
    report: "Baixar Relatório de Desempenho",
    guide: "Baixar Guia de Estudos",
    building: "Preparando Guia de Estudos…",
    error: "Não foi possível carregar seus downloads. Tente novamente.",
  },
  "es-419": {
    menu: "Mis Informes y Descargas",
    heading: "Sus Informes y Guías de Estudio",
    intro: "Sus Informes de Desempeño y Guías de Estudio están disponibles para descargar durante 30 días después de cada entrevista completada. Los archivos se eliminan permanentemente cuando termina el período de retención.",
    back: "Volver a la práctica de entrevista",
    empty: "No tiene informes ni guías de estudio retenidos disponibles para descargar.",
    quick: "Práctica rápida",
    advanced: "Práctica avanzada",
    repractice: "Nueva práctica",
    completed: "Completada",
    expires: "Disponible hasta",
    report: "Descargar Informe de Desempeño",
    guide: "Descargar Guía de Estudio",
    building: "Preparando Guía de Estudio…",
    error: "No pudimos cargar sus descargas. Inténtelo de nuevo.",
  },
  "fr-CA": {
    menu: "Mes rapports et téléchargements",
    heading: "Vos rapports et guides d’étude",
    intro: "Vos rapports de rendement et guides d’étude peuvent être téléchargés pendant 30 jours après chaque entrevue terminée. Les fichiers sont supprimés définitivement à la fin de la période de conservation.",
    back: "Retour à la pratique d’entrevue",
    empty: "Vous n’avez aucun rapport ni guide d’étude conservé disponible au téléchargement.",
    quick: "Pratique rapide",
    advanced: "Pratique avancée",
    repractice: "Nouvelle pratique",
    completed: "Terminée",
    expires: "Disponible jusqu’au",
    report: "Télécharger le rapport de rendement",
    guide: "Télécharger le guide d’étude",
    building: "Préparation du guide d’étude…",
    error: "Impossible de charger vos téléchargements. Veuillez réessayer.",
  },
};

function fmtDate(value, lang) {
  if (!value) return "—";
  try {
    const locale = lang === "pt-BR" ? "pt-BR" : lang === "es-419" ? "es" : lang === "fr-CA" ? "fr-CA" : "en-CA";
    return new Intl.DateTimeFormat(locale, { year: "numeric", month: "short", day: "numeric" }).format(new Date(value));
  } catch {
    return String(value);
  }
}

export default function Downloads() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const lang = normalizeLang(user?.preferred_language);
  const t = COPY[lang] || COPY.en;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [building, setBuilding] = useState(null);

  const baseURL = useMemo(() => api.defaults.baseURL || "/api", []);

  useEffect(() => {
    let alive = true;
    api.get("/my-downloads")
      .then(({ data }) => {
        if (alive) setItems(data?.items || []);
      })
      .catch(() => {
        if (alive) toast.error(t.error);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
  }, [t.error]);

  const downloadStudyGuide = async (item) => {
    if (building) return;
    const href = `${baseURL}/sessions/${item.session_id}/study-plan.pdf`;
    if (item.study_guide_ready) {
      window.open(href, "_blank", "noopener,noreferrer");
      return;
    }
    setBuilding(item.session_id);
    try {
      await api.post(`/sessions/${item.session_id}/study-plan`);
      setItems((rows) => rows.map((row) => row.session_id === item.session_id ? { ...row, study_guide_ready: true } : row));
      window.open(href, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t.error);
    } finally {
      setBuilding(null);
    }
  };

  return (
    <div className="min-h-screen bg-[color:var(--bg-base)]" data-testid="downloads-page">
      <header className="border-b border-[color:var(--border-stone)] bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/brand/logo.png" alt="My English Coach Interview Simulator logo" className="h-[63px] w-[63px] object-contain" />
            <div className="font-semibold tracking-tight">My English Coach Interview Simulator</div>
          </div>
          <UserMenu />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-10 py-8 md:py-12">
        <button type="button" onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-sm text-[color:var(--brand)] hover:underline">
          <ArrowLeft size={16} /> {t.back}
        </button>

        <h1 className="mt-7 text-3xl sm:text-4xl font-semibold tracking-tight">{t.heading}</h1>
        <p className="mt-4 max-w-3xl text-[color:var(--text-secondary)] leading-relaxed">{t.intro}</p>

        {loading ? (
          <div className="mt-10 text-sm text-[color:var(--text-secondary)]">…</div>
        ) : items.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-[color:var(--border-stone)] bg-white p-6 text-sm text-[color:var(--text-secondary)]">
            {t.empty}
          </div>
        ) : (
          <div className="mt-10 space-y-4">
            {items.map((item) => {
              const format = item.is_repractice
                ? t.repractice
                : item.practice_format === "advanced" ? t.advanced : t.quick;
              const reportHref = `${baseURL}/sessions/${item.session_id}/report.pdf`;
              return (
                <section key={item.session_id} className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-5 sm:p-6" data-testid={`download-item-${item.session_id}`}>
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-flex rounded-full bg-[color:var(--brand)]/10 px-3 py-1 text-xs font-medium text-[color:var(--brand)]">{format}</span>
                        <span className="text-xs text-[color:var(--text-secondary)]">{t.completed}: {fmtDate(item.completed_at, lang)}</span>
                      </div>
                      <div className="mt-3 text-sm text-[color:var(--text-secondary)]">
                        {t.expires}: <span className="font-medium text-[color:var(--text-primary)]">{fmtDate(item.expires_at, lang)}</span>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2 lg:shrink-0">
                      <a href={reportHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white px-5 py-2.5 text-sm font-medium">
                        <FileText size={17} /> <DownloadSimple size={16} /> {t.report}
                      </a>
                      <button type="button" onClick={() => downloadStudyGuide(item)} disabled={building === item.session_id} className="inline-flex items-center justify-center gap-2 rounded-full border border-[color:var(--brand)] text-[color:var(--brand)] hover:bg-[color:var(--brand)]/5 px-5 py-2.5 text-sm font-medium disabled:opacity-50">
                        <BookOpen size={17} /> <DownloadSimple size={16} /> {building === item.session_id ? t.building : t.guide}
                      </button>
                    </div>
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
